import { useMemo } from "react";
import type { BrandColorConfig } from "@wondermaps/db/schema";
import type { ThemeConfig } from "@wondermaps/maps";

export interface AccentColorResolved {
  // Existing boolean-gated accent slots
  categoryPills: string | null;
  drawerHandle: string | null;
  searchHeaderIcons: string | null;
  directionsUI: string | null;
  poiCtaButton: string | null;
  poweredByFooter: string | null;
  // PR5 boolean-gated slots (menuAccents removed in hard cutover)
  bottomNavActive: string | null;
  morePageCtaSecondary: string | null;
  menuIcons: string | null;
  socialCardBranded: string | null;
  // PR5 opacity-resolved bg slots — color-mix string when slider non-null, else null
  socialCardBg: string | null;
  /**
   * Ungated org/theme primary color — primary CTA fallback. Always reflects the
   * cascade `(theme.useAsVisitorAccent && theme.colors.primary) ?? brandColor`,
   * regardless of brandColorConfig toggles.
   */
  brandPrimary: string | null;
}

const NULL_RESULT: AccentColorResolved = {
  categoryPills: null,
  drawerHandle: null,
  searchHeaderIcons: null,
  directionsUI: null,
  poiCtaButton: null,
  poweredByFooter: null,
  bottomNavActive: null,
  morePageCtaSecondary: null,
  menuIcons: null,
  socialCardBranded: null,
  socialCardBg: null,
  brandPrimary: null,
};

const TOGGLE_KEYS: (keyof BrandColorConfig & keyof AccentColorResolved)[] = [
  "categoryPills",
  "drawerHandle",
  "searchHeaderIcons",
  "directionsUI",
  "poiCtaButton",
  "poweredByFooter",
  "bottomNavActive",
  "morePageCtaSecondary",
  "menuIcons",
  "socialCardBranded",
];

interface Branding {
  brandColor?: string | null;
  brandColorConfig?: BrandColorConfig | null;
}

/**
 * Resolve accent color per touchpoint.
 *
 * Cascade (PR5 — org-first): if the map theme has opted in via
 * `useAsVisitorAccent`, its `colors.primary` overrides the org brand color on
 * this map only. Otherwise the org `brandColor` is used everywhere.
 *   (theme.useAsVisitorAccent && theme.colors.primary) ?? branding.brandColor ?? null
 *
 * Toggle slots: gate is brandColorConfig[key] === true
 * Opacity slots: brandColorConfig[opacityKey] is number 0–100 (null = off)
 */
export function useAccentColor(
  branding: Branding | null | undefined,
  themeConfig: ThemeConfig | undefined,
): AccentColorResolved {
  return useMemo(() => {
    if (!branding) return NULL_RESULT;

    const themeOverride =
      themeConfig?.useAsVisitorAccent === true
        ? themeConfig?.colors?.primary ?? null
        : null;
    const baseColor = themeOverride ?? branding.brandColor ?? null;

    // brandPrimary tracks the resolved base color regardless of brandColorConfig
    // (primary CTA needs it even when no per-slot toggles are configured).
    const result = { ...NULL_RESULT, brandPrimary: baseColor };

    if (!baseColor) return result;

    const config = branding.brandColorConfig;
    if (!config) return result;

    for (const key of TOGGLE_KEYS) {
      if (config[key] === true) {
        (result as Record<string, string | null>)[key] = baseColor;
      }
    }

    if (typeof config.socialCardBgOpacity === "number") {
      result.socialCardBg = `color-mix(in srgb, ${baseColor} ${config.socialCardBgOpacity}%, transparent)`;
    }

    return result;
  }, [
    branding?.brandColor,
    branding?.brandColorConfig,
    themeConfig?.colors?.primary,
    themeConfig?.useAsVisitorAccent,
  ]);
}
